import React from 'react';
import { type TextRoleStyle } from './semanticTextRoles';
import { useImageSpaceReserve } from './imageSpaceReserve';

/**
 * WEB-926, позиция 6 — бинарная карточка предпочтений (референс 12min:
 * «Is this book interesting to you?» + крупная обложка + две карточки-ответа
 * ✕ No / ✓ Yes ~150×115, r16, рамка 1px; серия таких экранов ощущается как
 * отдельная игра и собирает вкусы).
 *
 * ЭТО ВОПРОС: клик пишет values[blockId] = 'no' | 'yes' — значение уходит в
 * SCREEN_ANSWER (ANSWER_BLOCK_TYPES) и доступно условиям ветвления. Подписи
 * ответов авторские и в логике не участвуют.
 *
 * Обложка опциональна: без картинки рисуется эмодзи-заглушка, чтобы карточка
 * не разъезжалась и в разметке не оставалось битого <img>.
 */

export type PreferenceAnswer = 'no' | 'yes';

interface AnswerSpec {
  key: PreferenceAnswer;
  glyph: string;
  color: string;
}

const ANSWERS: readonly AnswerSpec[] = [
  { key: 'no', glyph: '✕', color: 'var(--color-warning, #faad14)' },
  { key: 'yes', glyph: '✓', color: 'var(--color-success, #52c41a)' },
];

interface PreferenceCardBlockProps {
  blockId: string;
  /** Вопрос над карточкой (может быть rich-узлом). */
  question?: React.ReactNode;
  imageUrl?: string;
  emoji?: string;
  /** Подпись под обложкой; она же alt картинки. */
  caption?: string;
  labels: Record<PreferenceAnswer, string | undefined>;
  /**
   * WEB-1692: накладка на ВОПРОС (роль «главный текст»). Пустой объект =
   * сегодняшний вид: зашитые 20px и цвет поверхности блока.
   */
  titleStyle?: TextRoleStyle;
  /**
   * WEB-1692: накладка на ВТОРИЧНЫЕ ТЕКСТЫ — подпись под картинкой и подписи
   * обоих ответов ОДНОЙ настройкой (решение записано в semanticTextRoles.ts).
   * Пустой объект = сегодняшний вид (15px/14px).
   */
  secondaryStyle?: TextRoleStyle;
  value: string;
  onSelect: (value: PreferenceAnswer) => void;
}

const NO_ROLE_STYLE: TextRoleStyle = {};

export function PreferenceCardBlock({
  blockId,
  question,
  imageUrl,
  emoji,
  caption,
  labels,
  titleStyle = NO_ROLE_STYLE,
  secondaryStyle = NO_ROLE_STYLE,
  value,
  onSelect,
}: PreferenceCardBlockProps): React.ReactElement {
  const hasImage = typeof imageUrl === 'string' && imageUrl.trim().length > 0;
  // Починка ревизии: без onError сломанный адрес держал резерв места
  // (aspect-ratio запасной пропорции) НАВСЕГДА — пустая коробка во всю
  // ширину вместо контента. Один и тот же приём, что и в остальных трёх
  // местах (imageSpaceReserve.ts).
  const { style: imageStyle, onError: onImageError } = useImageSpaceReserve(
    {
      maxWidth: 200,
      width: '100%',
      display: 'block',
      margin: '0 auto 12px',
      borderRadius: 'var(--radius-image, 8px)',
    },
    imageUrl,
  );

  return (
    <div
      data-testid="preference-card"
      data-block-id={blockId}
      style={{ marginBottom: 16, textAlign: 'center' }}
    >
      {question && (
        <div
          data-testid="preference-card-question"
          // WEB-1692: слой автора спредится ПОСЛЕ прежних строк. Пусто = `{}`
          // — inline-стиль старой воронки не меняется ни на байт.
          style={{
            fontSize: 20,
            fontWeight: 700,
            lineHeight: 1.3,
            marginBottom: 16,
            ...titleStyle,
          }}
        >
          {question}
        </div>
      )}
      {hasImage ? (
        <img
          data-testid="preference-card-image"
          src={imageUrl}
          alt={caption || ''}
          decoding="async"
          // WEB-1994: место под картинку зарезервировано до загрузки; онError
          // снимает резерв (см. useImageSpaceReserve) — починка ревизии.
          style={imageStyle}
          onError={onImageError}
        />
      ) : (
        <div
          data-testid="preference-card-emoji"
          aria-hidden="true"
          style={{ fontSize: 64, lineHeight: 1.2, marginBottom: 12 }}
        >
          {emoji || '📄'}
        </div>
      )}
      {caption && (
        <div
          data-testid="preference-card-caption"
          style={{
            fontSize: 15,
            fontWeight: 600,
            marginBottom: 20,
            ...secondaryStyle,
          }}
        >
          {caption}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 'var(--option-gap, 12px)',
        }}
      >
        {ANSWERS.map((answer) => {
          const selected = value === answer.key;
          return (
            <button
              key={answer.key}
              type="button"
              data-testid={`preference-choice-${answer.key}`}
              data-selected={selected ? 'true' : 'false'}
              aria-pressed={selected}
              onClick={() => onSelect(answer.key)}
              style={{
                flex: '0 1 150px',
                minHeight: 'var(--option-h, 96px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: 'var(--option-pad-y, 12px) var(--option-pad-x, 16px)',
                borderRadius: 'var(--radius-option, 16px)',
                // Форма и толщина рамки — из пака; выбранный вариант
                // подсвечивается цветом ответа (он несёт смысл да/нет).
                border: `var(--option-border-width, 1px) solid ${
                  selected ? answer.color : 'var(--option-border-color, #d9d9d9)'
                }`,
                background: selected
                  ? `color-mix(in srgb, ${answer.color} 14%, transparent)`
                  : 'var(--color-bg-surface, transparent)',
                cursor: 'pointer',
                font: 'inherit',
                color: 'inherit',
                transition: 'background-color var(--transition-ms, 0ms) ease',
              }}
            >
              <span aria-hidden="true" style={{ fontSize: 24, color: answer.color }}>
                {answer.glyph}
              </span>
              {labels[answer.key] && (
                <span
                  data-testid={`preference-choice-label-${answer.key}`}
                  style={{ fontSize: 14, ...secondaryStyle }}
                >
                  {labels[answer.key]}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
